// core
import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
// models
import {
  OrderStatus,
  QueueInfo, StatusesFromServer,
} from '../../../../core/models/order.model';
// stores
import {OrderStore} from '../../../../core/stores/order.store';
import {RestaurantStore} from '../../../../core/stores/restaurant.store';
// services
import {PaymentService} from '../../../../core/services/payment.service';
import {OrdersStatusService} from '../../services/orders-status.service';
import {SoundNotificationsService} from '../../../../core/services/sound-notifications.service';
// static methods
import {HeaderComponent} from '../../../../shared/components/header/header.component';
// environment
import {environment} from '../../../../../environments/environment';
// rxjs
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-orders-status',
  templateUrl: './orders-status.component.html',
  styleUrls: ['./orders-status.component.scss']
})
export class OrdersStatusComponent implements OnInit, OnDestroy {

  public readonly locationId: string;
  public readonly isKiosk: boolean = environment.isKiosk;
  public readonly isKitchen: boolean = environment.isKitchen;
  // TODO: remove
  // public readonly isKitchen = true;

  public isEmptyOrders: boolean;
  public queueInfo: QueueInfo;
  public ordersQuantity: number;
  public ordersStatus: OrderStatus[];
  public currentOrder: OrderStatus;
  public isShowModal: boolean;
  public isLoadingStatuses: boolean;
  public isLoadingStatusesUpdate: boolean;

  private readonly TIME_TO_REFRESH = 30000;
  private readonly isOnlyPending = true;
  private readonly newOrderStatus = StatusesFromServer[0];
  private readonly pathToSoundNotification = 'assets/sound-notifications/SimpleHigh.mp3';

  private refreshIntervalHandler: number;
  private soundIntervalHandler: number;
  private isHaveNewOrders: boolean;
  private destroyed$ = new Subject<void>();

  public get showHeader(): boolean {
    return !this.isEmptyOrders && this.queueInfo != null;
  }

  constructor(
    public orderStore: OrderStore,
    private paymentService: PaymentService,
    private router: Router,
    private route: ActivatedRoute,
    private restaurantStore: RestaurantStore,
    public ordersStatusService: OrdersStatusService,
    public soundNotification: SoundNotificationsService
  ) {
    this.setQueueInfo();
    this.locationId = this.route.snapshot.params.locationId;
  }

  public ngOnInit(): void {
    this.checkQueueInfoIsEmpty();
    this.getIsLoading();
    this.setIntervalForStatus(this.locationId, this.isOnlyPending);
    this.orderStore.bootstrapAllPendingOrderStatus(this.locationId, this.isOnlyPending);
    this.orderStore.pendingOrdersStatus$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        orders => {
          if (orders) {
            this.ordersStatus = orders;
            this.ordersQuantity = orders.length;
            this.isEmptyOrders = orders.length === 0;

            if (this.isKitchen) {
              this.isHaveNewOrders = this.checkIsHaveNewOrders(orders);
              this.initSoundNotifications(this.pathToSoundNotification)
                .catch(error => console.error(error));
            }
          }
        }
      );
  }

  public ngOnDestroy(): void {
    if (this.refreshIntervalHandler) {
      clearInterval(this.refreshIntervalHandler);
    }
    if (this.soundIntervalHandler) {
      clearInterval(this.soundIntervalHandler);
    }
    this.destroyed$.next();
  }

  public updateOrderStatus(statusForUpdate: string, locationId: string, orderId: string): void {
    this.ordersStatusService.updateOrderStatus(statusForUpdate, locationId, orderId);
  }

  public goPrevPage(): void {
    window.history.back();
  }

  public closeModal(value: boolean): void {
    this.isShowModal = value;
  }

  private setIntervalForStatus(locationId: string, isOnlyPending: boolean) {
    this.refreshIntervalHandler = window.setInterval((function (self: any) {
      return function () {
        self.orderStore.bootstrapAllPendingOrderStatus(locationId, isOnlyPending);
      };
    })(this), this.TIME_TO_REFRESH);
  }

  private setQueueInfo(): void {
    const navigationExtrasState = this.router.getCurrentNavigation().extras.state;

    if (navigationExtrasState) {
      this.queueInfo = navigationExtrasState.queueInfo;
      localStorage.setItem('queueInfo', JSON.stringify(this.queueInfo));
    } else {
      this.queueInfo = localStorage.getItem('queueInfo') ?
        JSON.parse(localStorage.getItem('queueInfo')) :
        null;
    }
  }

  private checkQueueInfoIsEmpty() {
    this.restaurantStore
      .restaurantAvailableStatus$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(restaurantAvailableStatus => this.queueInfo = HeaderComponent.getQueueInfo(restaurantAvailableStatus));
  }

  private getIsLoading(): void {
    this.orderStore.isLoadingStatuses$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isLoading => this.isLoadingStatuses = isLoading);
    this.orderStore.isLoadingStatusesUpdate$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(isLoading => this.isLoadingStatusesUpdate = isLoading);
  }

  private checkIsHaveNewOrders(orders: OrderStatus[]): boolean {
    if (orders.length > 0) {
      let result: boolean;
      orders.forEach(order => {
        if (order.status === this.newOrderStatus) {
          result = true;
        }
      });
      return result ? result : null;
    }
  }

  private async initSoundNotifications(pathToSoundFile: string) {
    const id = 'newOrderNotification';
    await this.soundNotification.preloadAudio(id, pathToSoundFile);

    if (this.isHaveNewOrders) {
      await this.soundNotification.playAudio(id);

      if (!this.soundIntervalHandler) {
        this.soundIntervalHandler = window.setInterval(() => {
          this.soundNotification.playAudio(id);
        }, this.TIME_TO_REFRESH);
      }

    } else {
      clearInterval(this.soundIntervalHandler);
      this.soundIntervalHandler = null;
    }
  }
}
